/**
 * Shortpay route-page surface (FE-SP-002B).
 *
 * Each page mounts an already-merged feature presentation slice with
 * fixture-backed data and no network calls. `SHORTPAY_ROUTE_ELEMENTS` is keyed
 * by the shared route registry in lib/navigation so App.tsx mounts routes and
 * the shell nav / ownership checks against the exact same paths.
 *
 * Every page element is lazy-loaded so BeneCRM users never download the
 * unreachable Shortpay feature graph (dashboard/ECharts, onboarding,
 * statements, matching, casework, recovery) and the eager entry chunk stays
 * small — the production build used to OOM under the eagerly imported barrel.
 *
 * API integration is deferred to FE-SP-003…FE-SP-008.
 */
import { createElement, lazy } from "react";
import type { ComponentType, ReactNode } from "react";

import type { ShortpayRoute } from "@/lib/navigation";

// Lazy page elements are code-split into their own chunks and rendered inside
// a Suspense boundary that App.tsx wraps around the routed tree.
function lazyPageElement(
  loader: () => Promise<Record<string, ComponentType>>,
  exportName: string,
): ReactNode {
  return createElement(
    lazy(async () => {
      const page = await loader();
      return { default: page[exportName] };
    }),
  );
}

// One lazy element per surfaced registry route, so App.tsx can `map` over
// SHORTPAY_ROUTES and stay in lock-step with the single source of truth.
export const SHORTPAY_ROUTE_ELEMENTS: Record<ShortpayRoute, ReactNode> = {
  "/shortpay": lazyPageElement(
    () => import("./ShortpayDashboardPage"),
    "ShortpayDashboardPage",
  ),
  "/shortpay/onboarding": lazyPageElement(
    () => import("./ShortpayOnboardingPage"),
    "ShortpayOnboardingPage",
  ),
  "/shortpay/statements": lazyPageElement(
    () => import("./ShortpayStatementsPage"),
    "ShortpayStatementsPage",
  ),
  "/shortpay/matching": lazyPageElement(
    () => import("./ShortpayMatchingPage"),
    "ShortpayMatchingPage",
  ),
  "/shortpay/exceptions": lazyPageElement(
    () => import("./ShortpayExceptionsPage"),
    "ShortpayExceptionsPage",
  ),
  "/shortpay/recovery": lazyPageElement(
    () => import("./ShortpayRecoveryPage"),
    "ShortpayRecoveryPage",
  ),
  "/shortpay/settings": lazyPageElement(
    () => import("./ShortpaySettingsPage"),
    "ShortpaySettingsPage",
  ),
};
export { SHORTPAY_DASHBOARD_FIXTURE, SHORTPAY_MATCHING_ROWS } from "./fixtures";
